import axios, { AxiosInstance } from 'axios';
import { PublicClientApplication } from '@azure/msal-browser';
import { loginRequest } from '../auth/authConfig';

// Global axios instance with token interceptor
let authInstance: AxiosInstance | null = null;

export const setupAxiosInterceptor = (msalInstance: PublicClientApplication) => {
    if (authInstance) {
        return authInstance;
    }

    authInstance = axios.create();

    // Request interceptor to automatically add bearer token
    authInstance.interceptors.request.use(
        async (config) => {
            try {
                const accounts = msalInstance.getAllAccounts();
                
                if (accounts.length === 0) {
                    throw new Error('No authenticated accounts found');
                }

                const tokenResponse = await msalInstance.acquireTokenSilent({
                    ...loginRequest,
                    account: accounts[0],
                });

                config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
                return config;
            } catch (error) {
                console.error('Failed to acquire token:', error);
                
                // Try to acquire token interactively if silent acquisition fails
                try {
                    const interactiveResponse = await msalInstance.acquireTokenPopup(loginRequest);
                    config.headers.Authorization = `Bearer ${interactiveResponse.accessToken}`;
                    return config;
                } catch (interactiveError) {
                    console.error('Interactive token acquisition failed:', interactiveError);
                    return Promise.reject(interactiveError);
                }
            }
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor to handle token expiration
    authInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            if (error.response?.status === 401) {
                // Token might be expired, try to refresh
                try {
                    const accounts = msalInstance.getAllAccounts();
                    if (accounts.length > 0) {
                        await msalInstance.acquireTokenSilent({
                            ...loginRequest,
                            account: accounts[0],
                            forceRefresh: true
                        });
                        
                        // Retry the original request
                        return authInstance!.request(error.config);
                    }
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                }
            }
            return Promise.reject(error);
        }
    );

    return authInstance;
};

export const getAuthenticatedAxios = () => {
    if (!authInstance) {
        throw new Error('Axios interceptor not initialized. Call setupAxiosInterceptor first.');
    }
    return authInstance;
};

export default authInstance;
